<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { DynamicForm } from '../../dynamic-form'
import { getForm } from '../../../core/services/forms'
import { onBeforeMount, ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { DLoading } from 'front-library'
import {
  getPayrollConcept,
  createPayrollConcept,
  updatePayrollConcept,
  getFiscalRule
} from '../../../core/services/payroll-concepts'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

interface Field {
  name: string,
  value: any
}

/* router, notifications */
const notifications = useNotificationsStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const customForm = ref<any>({})
const form = ref<typeof DynamicForm | null>(null)
let model: any = {}

const onSubmit = async (model: any) => {
  delete model.showFiscalRuleFields
  if (model.percentage === undefined || model.percentage === '')
    model.percentage = null
  loading.value = true
  let saved = false
  try {
    if (!route.params.id) {
      saved = await createPayrollConcept(model)
    } else {
      saved = await updatePayrollConcept(model, `${route.params.id as string}`)
    }
    if (saved) {
      loading.value = false
      notifications.addNotification({
        title: !route.params.id
          ? t('notification.created')
          : t('notification.edited'),
        text: t('payrollConcepts.notification.action', [
          !route.params.id
            ? t('notification.wasCreated')
            : t('notification.wasEdited')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'ListPayrollConcepts' })
    }
  } catch (error: any) {
    notificationError(error, 'payrollConcepts', t, notifications, loading)
  }
}

const getEntity = async () => {
  if (!route.params.id) return
  model = await getPayrollConcept(route.params.id as string)
  if (model === undefined) {
    notifications.addNotification({
      title: t('notification.notFound'),
      text: t('payrollConcepts.notification.error'),
      type: 'error',
      time: 10000
    })
    back()
  }
}

const changeShowFiscalRuleFields = async (field: Field) => {
  if (field.name === 'custom:fiscalRule') {
    const check = field.value ? await getFiscalRule(field.value) : false;
    if (customForm.value) {
      form.value?.setModelValue('custom:showFiscalRuleFields', check)
    }
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListPayrollConcepts' })
}

onBeforeMount(async () => {
  loading.value = true
  try {
    await getEntity()
    customForm.value = await getForm({ entity: 'concepts' })
    if (route.params.id !== undefined) messageLoading.value = t('loading.data')
    loading.value = false
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('payrollConcepts.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'payrollConcepts', t, notifications, loading)
    }
  }
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <div class="h-full px-5">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="
            route.params.id == undefined
              ? t('payrollConcepts.create')
              : t('payrollConcepts.edit')
          " />
      </div>
    </div>
    <DynamicForm
      ref="form"
      v-if="customForm?.tabs"
      :form-schema="customForm"
      :initial-model="model"
      :title-btn-save="
        route.params.id === undefined
          ? t('form.button.save')
          : t('form.button.update')
      "
      @cancel="back"
      @submit="onSubmit" 
      @update-field="changeShowFiscalRuleFields"/>
  </div>
</template>
